'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useLeaveGuard } from '@/components/ui/dirty-guard';
import { apiPost } from '@/lib/api';
import PlayerCard from '@/components/player/PlayerCard';
import type { BasicPlayer } from '@/models/userModel';
import { authModel } from '@/models/authModel';

type SearchResult = {
  type: 'player';
  data: BasicPlayer;
};

interface GlobalSearchProps {
  onUserSelect?: (player: BasicPlayer) => void;
  placeholder?: string;
  federationId?: string;
  eventId?: string;
}

export default function GlobalSearch({
  onUserSelect,
  placeholder,
  federationId,
  eventId,
}: GlobalSearchProps) {
  const t = useTranslations('Search');
  const locale = useLocale();
  const router = useRouter();
  const { guard } = useLeaveGuard();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Player search requires a session (`/api/user/search` 401s without one). The shell
  // renders this on public routes too, so without this check a logged-out visitor gets a
  // search box that silently returns "no results" for everything.
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    authModel
      .getSession()
      .then((session) => {
        if (active) setSignedIn(!!session);
      })
      .catch(() => {
        if (active) setSignedIn(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    if (!signedIn || !query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        // Search players for now - will expand to global search later
        const response = await apiPost<{ rows: BasicPlayer[] }>('/api/user/search', {
          search: query,
          federationId,
          eventId,
        });
        const players = response?.rows || [];

        const searchResults: SearchResult[] = players.map((player) => ({
          type: 'player',
          data: player,
        }));

        setResults(searchResults);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [eventId, federationId, query, signedIn]);

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'player') {
      if (onUserSelect) {
        // If onUserSelect is provided, use it for admin selection
        onUserSelect(result.data);
        setOpen(false);
        setQuery('');
      } else {
        // Otherwise, navigate to user profile (default behavior)
        const slug = result.data.username;
        void guard(() => router.push(`/${locale}/user/${slug}`));
        setOpen(false);
        setQuery('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Hidden entirely for signed-out visitors rather than shown and permanently empty.
  if (signedIn === false) return null;

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t?.('placeholder') || 'Search players...'}
          className="border-border bg-background text-foreground placeholder-foreground/50 focus:border-primary focus:ring-primary h-9 w-full rounded-md border px-3 py-1 text-sm focus:ring-1 focus:outline-none"
        />
        <div className="absolute top-1/2 right-2 -translate-y-1/2">
          {loading ? (
            <div className="border-t-primary h-4 w-4 animate-spin rounded-full border-2 border-gray-300" />
          ) : (
            <svg
              className="text-foreground/50 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>
      </div>

      {open && query.trim() && (
        <div className="border-border bg-background absolute top-full left-0 z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border shadow-lg">
          {results.length === 0 && !loading ? (
            <div className="text-foreground/60 px-3 py-2 text-sm">
              {t?.('noResults') || 'No results found'}
            </div>
          ) : (
            <ul>
              {results.map((result, index) => (
                <li key={`${result.type}-${index}`}>
                  <div
                    className="hover:bg-primary/50 cursor-pointer px-3 py-2"
                    onClick={() => handleResultClick(result)}
                  >
                    <PlayerCard
                      player={result.data}
                      showRating={true}
                      showUsername={false}
                      size="compact"
                      clickable={false}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
